<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { computed, watch } from 'vue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'

interface BrandOption {
    id: number
    label: string
    products: { id: number, label: string }[]
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    brands: BrandOption[]
    design: {
        brand_id: number
        brand_product_id: number | null
        name: string
        link: string
        note: string | null
        link_updated_at: string | null
    } | null
    submitRoute: { name: string, parameters?: any[], method?: string }
}>()

const form = useForm({
    brand_id: props.design?.brand_id ?? props.brands[0]?.id ?? null,
    brand_product_id: props.design?.brand_product_id ?? null,
    name: props.design?.name ?? '',
    link: props.design?.link ?? '',
    note: props.design?.note ?? '',
})

const selectedBrand = computed(() => props.brands.find(brand => brand.id === form.brand_id))

watch(() => form.brand_id, () => {
    const stillValid = selectedBrand.value?.products.some(product => product.id === form.brand_product_id)
    if (!stillValid) form.brand_product_id = null
})

const submit = () => {
    form[props.submitRoute.method ?? 'post'](route(props.submitRoute.name, props.submitRoute.parameters))
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-auto max-w-2xl px-4 py-6">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <div class="grid gap-4">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Brand <span class="text-red-500">*</span></label>
                    <select v-model="form.brand_id" class="w-full rounded-md border-gray-300 text-sm">
                        <option v-for="brand in brands" :key="brand.id" :value="brand.id">{{ brand.label }}</option>
                    </select>
                    <div class="mt-1 text-xs text-gray-400">Satu desain hanya untuk satu brand.</div>
                    <div v-if="form.errors.brand_id" class="mt-1 text-xs text-red-500">{{ form.errors.brand_id }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Produk brand</label>
                    <select v-model="form.brand_product_id" class="w-full rounded-md border-gray-300 text-sm">
                        <option :value="null">Semua produk brand ini</option>
                        <option v-for="product in selectedBrand?.products ?? []" :key="product.id" :value="product.id">
                            {{ product.label }}
                        </option>
                    </select>
                    <div v-if="form.errors.brand_product_id" class="mt-1 text-xs text-red-500">{{ form.errors.brand_product_id }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Nama desain <span class="text-red-500">*</span></label>
                    <input v-model="form.name" placeholder="Desain Lebaran 2026" class="w-full rounded-md border-gray-300 text-sm" />
                    <div v-if="form.errors.name" class="mt-1 text-xs text-red-500">{{ form.errors.name }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Link desain <span class="text-red-500">*</span></label>
                    <input v-model="form.link" placeholder="https://drive.google.com/..." class="w-full rounded-md border-gray-300 text-sm" />
                    <div class="mt-1 text-xs text-gray-400">
                        Link Google Drive (folder atau file).
                        <span v-if="design?.link_updated_at">Terakhir diupdate {{ useFormatTime(design.link_updated_at) }}.</span>
                    </div>
                    <div v-if="form.errors.link" class="mt-1 text-xs text-red-500">{{ form.errors.link }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Catatan</label>
                    <textarea v-model="form.note" rows="3" placeholder="Contoh: warna tutup pakai Pantone 185C" class="placeholder:opacity-70 w-full rounded-md border-gray-300 text-sm" />
                    <div class="mt-1 text-xs text-gray-400">
                        Catatan akan ditampilkan di halaman Manufacture dan dibaca oleh staff produksi.
                    </div>
                </div>
            </div>
        </section>

        <div class="mt-4 flex justify-end">
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </div>
</template>
